import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGetCompaniesByIdQuery } from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DD,
  DT,
  GridCol,
  GridRow,
  isDefinedString,
  Text,
} from '@client/toolkit';

export interface CompanyProfileCardProps {
  className?: string;
}

export function CompanyProfileCard({ className }: CompanyProfileCardProps) {
  const { t, ready } = useTranslation('companies');
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { id } = useParams<{ id: string }>();
  const { data: company, isFetching } = useGetCompaniesByIdQuery({ id: id ?? '' }, { skip: id == null });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" loading={!ready} variant="dense">
          {t('profile')}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div className="mb-4">
          <Text
            className="text-xl font-medium text-white"
            loading={isFetching}
            isEmpty={!isDefinedString(company?.name)}
          >
            {company?.name}
          </Text>
        </div>

        <GridRow cols={3} spacingY={1} spacingX={2} className="mb-4">
          <GridCol span={1}>
            <DT>{t('table.roles')}</DT>
          </GridCol>
          <GridCol span={2}>
            <DD loading={isFetching} isEmpty={!company?.roles?.length}>
              {company?.roles?.map((role) => t(`roles.${role.toLowerCase()}`)).join(', ')}
            </DD>
          </GridCol>
        </GridRow>

        <div className="mb-1">
          <div className="mb-4">
            <Text
              className="font-bold uppercase text-xs tracking-tight text-white"
              loading={!ready}
              SkeletonProps={{ className: 'w-[30%]' }}
            >
              {t('invoicingInformation')}
            </Text>
          </div>

          <GridRow cols={3} spacingY={1} spacingX={2} className="mb-4">
            <GridCol span={1}>
              <DT>{tGlobal('legalPerson.vatId')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.vatId)}>
                {company?.vatId}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tGlobal('legalPerson.invoicingAccount.integration')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.invoicingAccount?.integration)}>
                {tType(`InvoicingIntegration.${company?.invoicingAccount?.integration}`)}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tGlobal('legalPerson.invoicingAccount.identifier')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.invoicingAccount?.identifier)}>
                {company?.invoicingAccount?.identifier}
              </DD>
            </GridCol>
          </GridRow>

          <div className="mb-4">
            <Text
              className="font-bold uppercase text-xs tracking-tight text-white"
              loading={!ready}
              SkeletonProps={{ className: 'w-[30%]' }}
            >
              {t('contactInformation')}
            </Text>
          </div>

          <GridRow cols={3} spacingY={1} spacingX={2} className="mb-4">
            <GridCol span={1}>
              <DT>{t('form.website')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.website)}>
                {company?.website && (
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-primary hover:underline"
                  >
                    {company.website}
                  </a>
                )}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.emailAddress')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.emailAddress)}>
                {company?.emailAddress && (
                  <a href={`mailto:${company.emailAddress}`} className="text-brand-primary hover:underline">
                    {company.emailAddress}
                  </a>
                )}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.mobilePhone')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.mobilePhone)}>
                {company?.mobilePhone && (
                  <a href={`tel:${company.mobilePhone.replace(/\s/g, '')}`} className="text-brand-primary hover:underline">
                    {company.mobilePhone}
                  </a>
                )}
              </DD>
            </GridCol>
          </GridRow>

          <GridRow cols={3} spacingY={1} spacingX={2} className="mb-4">
            <GridCol span={1}>
              <DT>{t('form.address')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.businessAddress?.address)}>
                {company?.businessAddress?.address}
              </DD>
            </GridCol>
            <GridCol span={1}>
              <DT>{t('form.postalCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.businessAddress?.postalCode)}>
                {company?.businessAddress?.postalCode}
              </DD>
            </GridCol>
            <GridCol span={1}>
              <DT>{t('form.postalCity')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.businessAddress?.city)}>
                {company?.businessAddress?.city}
              </DD>
            </GridCol>
            <GridCol span={1}>
              <DT>{t('form.municipality')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.businessAddress?.municipality)}>
                {company?.businessAddress?.municipality}
              </DD>
            </GridCol>
          </GridRow>

          <div className="mb-4">
            <Text
              className="font-bold uppercase text-xs tracking-tight text-white"
              loading={!ready}
              SkeletonProps={{ className: 'w-[30%]' }}
            >
              {t('companyInformation')}
            </Text>
          </div>

          <GridRow cols={3} spacingY={1} spacingX={2}>
            <GridCol span={1}>
              <DT>{t('form.organizationForm')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.organizationForm)}>
                {company?.organizationForm}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.industryCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.industryCode)}>
                {company?.industryCode}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.institutionalSectorCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.institutionalSectorCode)}>
                {company?.institutionalSectorCode}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.numberOfEmployees')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={company?.numberOfEmployees == null}>
                {company?.numberOfEmployees}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.parentOrganizationNumber')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.parentOrganizationNumber)}>
                {company?.parentOrganizationNumber}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.entityRegistrationDate')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.entityRegistrationDate)}>
                {company?.entityRegistrationDate}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.isPartOfGroup')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={company?.isPartOfGroup == null}>
                {company?.isPartOfGroup == null ? null : company.isPartOfGroup ? t('form.yes') : t('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.languageCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.languageCode)}>
                {company?.languageCode}
              </DD>
            </GridCol>
          </GridRow>

          <div className="mb-4 mt-6">
            <Text
              className="font-bold uppercase text-xs tracking-tight text-white"
              loading={!ready}
              SkeletonProps={{ className: 'w-[30%]' }}
            >
              {t('statusInformation')}
            </Text>
          </div>

          <GridRow cols={3} spacingY={1} spacingX={2}>
            <GridCol span={1}>
              <DT>{t('form.isBankrupt')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={company?.isBankrupt == null}>
                {company?.isBankrupt == null ? null : company.isBankrupt ? t('form.yes') : t('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.isUnderLiquidation')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={company?.isUnderLiquidation == null}>
                {company?.isUnderLiquidation == null
                  ? null
                  : company.isUnderLiquidation
                  ? t('form.yes')
                  : t('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.isUnderForcedDissolution')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={company?.isUnderForcedDissolution == null}>
                {company?.isUnderForcedDissolution == null
                  ? null
                  : company.isUnderForcedDissolution
                  ? t('form.yes')
                  : t('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.registeredInVatRegistry')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={company?.registeredInVatRegistry == null}>
                {company?.registeredInVatRegistry == null
                  ? null
                  : company.registeredInVatRegistry
                  ? t('form.yes')
                  : t('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.vatRegistrationDate')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.vatRegistrationDate)}>
                {company?.vatRegistrationDate}
              </DD>
            </GridCol>
          </GridRow>

          <div className="mb-4 mt-6">
            <Text
              className="font-bold uppercase text-xs tracking-tight text-white"
              loading={!ready}
              SkeletonProps={{ className: 'w-[30%]' }}
            >
              {t('annualAccountsInformation')}
            </Text>
          </div>

          <GridRow cols={3} spacingY={1} spacingX={2}>
            <GridCol span={1}>
              <DT>{t('form.lastAnnualAccountsYear')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.lastAnnualAccountsYear)}>
                {company?.lastAnnualAccountsYear}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.activity')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.activity)}>
                {company?.activity}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.remarks')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.remarks)}>
                {company?.remarks}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{t('form.brregUrl')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!isDefinedString(company?.brregUrl)}>
                {company?.brregUrl && (
                  <a
                    href={company.brregUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-primary hover:underline"
                  >
                    {company.brregUrl}
                  </a>
                )}
              </DD>
            </GridCol>
          </GridRow>
        </div>
      </CardContent>
    </Card>
  );
}
